import I18n from 'i18n-js';

import { isLeapYear } from './utils';

function countLeapYears(fromYear, toYear) {
  let leapYears = 0;
  for (let i = fromYear; i <= toYear; i++) {
    if (isLeapYear(i)) leapYears += 1;
  }
  return leapYears;
}

const MINUTES_IN_YEAR = 525600;
const MINUTES_IN_QUARTER_YEAR = 131400;
const MINUTES_IN_THREE_QUARTERS_YEAR = 394200;

export default function distanceOfTimeInWords(fromTime, toTime = 0, { scope = 'datetime.distance_in_words', includeSeconds = false } = {}) {
  fromTime = new Date(fromTime);
  toTime = new Date(toTime);

  if (fromTime > toTime) [fromTime, toTime] = [toTime, fromTime];
  let distanceInMinutes = Math.round((toTime - fromTime) / 60000);
  let distanceInSeconds = Math.round((toTime - fromTime) / 1000);

  const t = (s, options = {}) => I18n.t(`${scope}.${s}`, options);

  if (distanceInMinutes >= 0 && distanceInMinutes <= 1) {
    if (!includeSeconds) {
      return distanceInMinutes === 0 ?
        t('less_than_x_minutes', { count: 1 }) :
        t('x_minutes', { count: distanceInMinutes });
    }

    if (distanceInSeconds >= 0 && distanceInSeconds <= 4 )  return t('less_than_x_seconds', { count: 5 });
    if (distanceInSeconds >= 5 && distanceInSeconds <= 9 )  return t('less_than_x_seconds', { count: 10 });
    if (distanceInSeconds >= 10 && distanceInSeconds <= 19) return t('less_than_x_seconds', { count: 20 });
    if (distanceInSeconds >= 20 && distanceInSeconds <= 39) return t('half_a_minute');
    if (distanceInSeconds >= 40 && distanceInSeconds <= 59) return t('less_than_x_minutes', { count: 1 });
    return t('x_minutes', { count: 1 });
  }
  if (distanceInMinutes >= 2 && distanceInMinutes < 45) {
    return t('x_minutes', { count: distanceInMinutes });
  }
  if (distanceInMinutes >= 45 && distanceInMinutes < 90) {
    return t('about_x_hours', { count: 1 });
  }
  // 90 mins up to 24 hours
  if (distanceInMinutes >= 90 && distanceInMinutes < 1440) {
    return t('about_x_hours', { count: Math.round(distanceInMinutes / 60) });
  }
  // 24 hours up to 42 hours
  if (distanceInMinutes >= 1440 && distanceInMinutes < 2520) {
    return t('x_days', { count: 1 });
  }
  // 42 hours up to 30 days
  if (distanceInMinutes >= 2520 && distanceInMinutes < 43200) {
    return t('x_days', { count: Math.round(distanceInMinutes / 1440) });
  }
  // 30 days up to 60 days
  if (distanceInMinutes >= 43200 && distanceInMinutes < 86400) {
    return t('about_x_months', { count: Math.round(distanceInMinutes / 43200) });
  }
  // 60 days up to 365 days
  if (distanceInMinutes >= 86400 && distanceInMinutes < 525600) {
    return t('x_months', { count: Math.round(distanceInMinutes / 43200) });
  }

  let fromYear = fromTime.getFullYear()
  if (fromTime.getMonth() >= 2) fromYear += 1;
  let toYear = toTime.getFullYear();
  if (toTime.getMonth() < 2) toYear -= 1;
  let leapYears = (fromYear > toYear) ? 0 : countLeapYears(fromYear, toYear);
  let minuteOffsetForLeapYear = leapYears * 1440;
  // Discount the leap year days when calculating year distance.
  // e.g. if there are 20 leap year days between 2 dates having the same day
  // and month then the based on 365 days calculation
  // the distance in years will come out to over 80 years when in written
  // English it would read better as about 80 years.
  let minutesWithOffset = distanceInMinutes - minuteOffsetForLeapYear;
  let remainder = (minutesWithOffset % MINUTES_IN_YEAR);
  let distanceInYears = Math.floor(minutesWithOffset / MINUTES_IN_YEAR);
  if (remainder < MINUTES_IN_QUARTER_YEAR) {
    return t('about_x_years', { count: distanceInYears });
  }
  if (remainder < MINUTES_IN_THREE_QUARTERS_YEAR) {
    return t('over_x_years', { count: distanceInYears })
  }

  return t('almost_x_years', { count: distanceInYears + 1 });
}

export function timeAgoInWords(fromTime, options = {}) {
  return distanceOfTimeInWords(fromTime, new Date(), options);
}
